/* ••[1]••••••••••••••••••••••••• VanillaEventsProvider.ts •••••••••••••••••••••••••••••• */
import VanillaProvider from "./VanillaProvider";
import Event, { EventPaginated } from "../../../interfaces/Event";
import FetchSettings from "../../../interfaces/FetchSettings";
import { VanillaEvent } from "../../../interfaces/VanillaApi";
import { SortOption } from "../../../interfaces/Pagination";
import { linkParser } from "../../../utilities";

export default class VanillaEventsProvider extends VanillaProvider {
  public async getEvents(
    dateStart: string,
    dateEnd: string,
    categoryID: number,
    sort = SortOption.asc,
    returnPagination = false,
    limit = -1,
    page = -1
  ): Promise<Array<Event> | EventPaginated> {
    const normalizeDate = (date: string): string => {
      if (!date) return date;
      if (date.length === 10) return `${date}T00:00:00`;
      return date;
    };

    const normalizedDateStart = normalizeDate(dateStart);
    const normalizedDateEnd = normalizeDate(dateEnd);

    let links: string;
    const url = new URL(this.baseUrl + "events");
    const searchParams = new URLSearchParams();
    const dateFormatLength = 19;

    searchParams.append("expand", "true");
    searchParams.append("sort", sort.toString() + "dateStarts");
    searchParams.append("parentRecordType", "category");

    if (
      normalizedDateStart.length === dateFormatLength &&
      normalizedDateEnd.length === dateFormatLength &&
      normalizedDateStart === normalizedDateEnd
    ) {
      searchParams.append("dateStarts", "=" + normalizedDateStart);
    } else {
      if (normalizedDateStart.length === dateFormatLength) {
        searchParams.append("dateStarts", ">=" + normalizedDateStart);
      }
      if (normalizedDateEnd.length === dateFormatLength) {
        searchParams.append("dateEnds", "<" + normalizedDateEnd);
      }
    }
    if (categoryID > 0) {
      searchParams.append("parentRecordID", categoryID.toString());
    }
    if (limit > 0) {
      searchParams.append("limit", limit.toString());
    }
    if (page > 0) {
      searchParams.append("page", page.toString());
    }
    url.search = new URLSearchParams(searchParams).toString();
    const settings: FetchSettings = {
      method: "GET",
    };
    const response: Response = await this.request(
      url.toString(),
      settings,
      true
    );
    if (response.status !== 200) {
      throw "Failed call to get events. Status " + response.status;
    }
    const data: Array<VanillaEvent> = await response.json();
    if (returnPagination) {
      links = response.headers.get("link");
    }
    const events: Array<Event> = data.map((event: VanillaEvent) => {
      return {
        eventID: event.eventID,
        name: event.name,
        excerpt: event.excerpt,
        location: event.location,
        locationUrl: event.safeLocationUrl,
        ctaUrl: event.ctaUrl,
        dateStart: new Date(event.dateStarts),
        dateEnd: new Date(event.dateEnds),
        allDayEvent: event.allDayEvent,
        datePosted: new Date(event.dateInserted),
        postUser: {
          id: event.insertUser.userID,
          name: event.insertUser.name,
          profileUrl: event.insertUser.url,
        },
        url: event.url,
        attending: event.attending,
      };
    });
    return returnPagination
      ? { events: events, pagination: linkParser(links) }
      : events;
  }

  public async getUpcomingEvents(
    dateStart: string,
    dateEnd: string,
    categoryID: number,
    sort = SortOption.asc,
    limit = 3,
    page = 1
  ): Promise<Array<Event>> {
    const url = new URL(this.baseUrl + "events");
    const searchParams = new URLSearchParams();
    const dateFormatLength = 19;
    searchParams.append("expand", "true");
    searchParams.append("sort", sort.toString() + "dateStarts");
    searchParams.append("parentRecordType", "category");
    if (
      dateStart.length === dateFormatLength &&
      dateEnd.length === dateFormatLength &&
      dateStart === dateEnd
    ) {
      searchParams.append("dateStarts", "=" + dateStart);
    } else {
      if (dateStart.length === dateFormatLength) {
        searchParams.append("dateStarts", ">=" + dateStart);
      }
      if (dateEnd.length === dateFormatLength) {
        searchParams.append("dateEnds", "<" + dateEnd);
      }
    }
    if (categoryID > 0) {
      searchParams.append("parentRecordID", categoryID.toString());
    }
    if (limit > 0) {
      searchParams.append("limit", limit.toString());
    }
    if (page > 0) {
      searchParams.append("page", page.toString());
    }
    url.search = new URLSearchParams(searchParams).toString();
    const settings: FetchSettings = {
      method: "GET",
    };
    const response: Response = await this.request(
      url.toString(),
      settings,
      true
    );
    if (response.status !== 200) {
      throw "Failed call to get events. Status " + response.status;
    }
    const data: Array<VanillaEvent> = await response.json();
    const events: Array<Event> = data.map((event: VanillaEvent) => {
      return {
        eventID: event.eventID,
        name: event.name,
        excerpt: event.excerpt,
        location: event.location,
        locationUrl: event.safeLocationUrl,
        ctaUrl: event.locationUrl,
        dateStart: new Date(event.dateStarts),
        dateEnd: new Date(event.dateEnds),
        allDayEvent: event.allDayEvent,
        datePosted: new Date(event.dateInserted),
        postUser: {
          id: event.insertUser.userID,
          name: event.insertUser.name,
          profileUrl: event.insertUser.url,
        },
        url: event.url,
        attending: event.attending,
      };
    });
    return events;
  }

  public async rsvpEvent(
    rsvpOption: string,
    eventID: number
  ): Promise<boolean> {
    const url = new URL(this.baseUrl + `events/${eventID}/participants`);
    const body = { attending: rsvpOption, eventID: eventID };
    const settings: FetchSettings = {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "x-requested-with": "vanilla",
      },
    };
    const data: Event = await this.request(url.toString(), settings);
    return data.attending === rsvpOption;
  }
}
